import { createSlice } from '@reduxjs/toolkit';

import type { ObjectPrivilege } from 'cadenza/access-management/model/object-privilege.gen';
import type { ControllerViewOptionValuesDto } from 'cadenza/api-client/dto/controller-view-data-dto';
import type { DataProtectionDto } from 'cadenza/api-client/dto/data-protection-dto';
import type { ObjecttypesDataDependenciesDto } from 'cadenza/api-client/dto/objecttype-data-dependencies-dto';
import type { ProjectDto } from 'cadenza/api-client/dto/project-dto';
import type { RepositoryDto } from 'cadenza/api-client/dto/repository-dto';
import type { DisplayState } from 'cadenza/api-client/workbook-api';
import type { Command } from 'cadenza/api-client/workbook-commands';
import type { OnDemandMapState } from 'cadenza/map/on-demand/on-demand-map.state';
import { onDemandMapReducers } from 'cadenza/map/on-demand/on-demand-map.state';
import type { WorkbookConfigurationDto } from 'cadenza/model/workbook-configuration';
import type { ObjecttypeId, ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id.gen';
import type { ProjectId, ProjectIdString } from 'cadenza/repository/model/project-id.gen';
import type { PublicationStage } from 'cadenza/repository/model/publication-stage.gen';
import type { WorkbookEmbeddingTargetId } from 'cadenza/repository/model/workbook-embedding-target-id.gen';
import type { WorkbookNameAndHash } from 'cadenza/repository/model/workbook-name-and-hash';
import type { WorkbookName } from 'cadenza/repository/model/workbook-name.gen';
import { getStore } from 'cadenza/store/cadenza-store';
import type { STATUS } from 'cadenza/store/state-utils';
import type { ReportDto } from 'cadenza/workbook-report/dto/report-dto';
import type { ReportId } from 'cadenza/workbook-report/dto/report-id';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { BasicObjecttypeReachabilityDto } from 'cadenza/workbook/api/basic-objecttype-reachability-dto';
import type { DataViewDto } from 'cadenza/workbook/api/data-view-dto';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import { type Objecttype } from 'cadenza/workbook/api/objecttype';
import type { ViewDto } from 'cadenza/workbook/api/view-dto';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { WorkbookEmbeddingTargetDto } from 'cadenza/workbook/api/workbook-embedding-target-dto';
import type { WorkbookRelation } from 'cadenza/workbook/api/workbook-relation';
import type { WorkbookVersion } from 'cadenza/workbook/api/workbook-version';
import type { WorksheetDto } from 'cadenza/workbook/api/worksheet-dto';
import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';
import type {
  ViewDrillThroughDto,
  WorksheetDrillThroughDto,
} from 'cadenza/workbook/drill-through/model/drill-through-dto';
import type { AnalysisContextHierarchyDto } from 'cadenza/workbook/model/analysis-context-hierarchy-dto';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import type { ConditionDto } from 'cadenza/workbook/model/condition-dto';
import type { ConditionGroupDto } from 'cadenza/workbook/model/condition-group-dto';
import type { ConditionGroupId } from 'cadenza/workbook/model/condition-group-id.gen';
import type { ConditionId } from 'cadenza/workbook/model/condition-id.gen';
import type { ConditionToViewsRelationsDto } from 'cadenza/workbook/model/condition-to-views-relations-dto';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import type { DataViewToDataViewConnectionStatusDto } from 'cadenza/workbook/model/data-view-to-data-view-connection-status-dto';
import type { HierarchyId } from 'cadenza/workbook/model/hierarchy-id.gen';
import type { HierarchyToViewsRelationsDto } from 'cadenza/workbook/model/hierarchy-to-views-relations-dto';
import type { ViewToDataViewConnectionStatusDto } from 'cadenza/workbook/model/view-to-data-view-connection-status-dto';
import type { WorkbookConfigDto } from 'cadenza/workbook/model/workbook-config-dto';
import type { WorkbookDataSourceDto } from 'cadenza/workbook/model/workbook-data-source-dto';
import type { WorkbookType } from 'cadenza/workbook/model/workbook-type.gen';
import type {
  WorkbookNotification,
  WorkbookNotificationId,
} from 'cadenza/workbook/notifications/workbook-notifications';
import type {
  SelectionInLayerDto,
  SelectionInOtherLayersDto,
} from 'cadenza/workbook/selection/model/selection-in-other-layers-dto';
import type { WorkbookSelectionDto } from 'cadenza/workbook/selection/model/workbook-selection-dto';
import { liveModeReducers } from 'cadenza/workbook/state/workbook-live-mode-reducers.state';
import type { WorkbookLiveModeState } from 'cadenza/workbook/state/workbook-live-mode.state';
import type { MapViewWorkbookState } from 'cadenza/workbook/state/workbook-map-view.state';
import { mapViewReducers } from 'cadenza/workbook/state/workbook-map-view.state';
import { type PanelById, panelReducers } from 'cadenza/workbook/state/workbook-panel.state';
import type { WorkbookTextViewState } from 'cadenza/workbook/state/workbook-text-view.state';
import { addTextViewExtraReducers } from 'cadenza/workbook/state/workbook-text-view.state';
import { createInitialWorkbookState } from 'cadenza/workbook/workbook-core-initial-state';
import { addCoreExtraReducers, coreReducers, fetchInitialState } from 'cadenza/workbook/workbook-core-reducers.state';
import { SLICE_PREFIX } from 'cadenza/workbook/workbook-core-slice.state';
import { type Settings } from 'cadenza/workbook/workbook-settings-util';

declare module 'cadenza/store/cadenza-store' {
  interface RootState {
    [SLICE_PREFIX]: WorkbookState;
  }
}

/**
 * Enum for the changed state of the workbook.
 */
type ChangedState = 'no' | 'minor' | 'major';

export type ViewMetadata = Record<string, unknown>;

export type ViewWithMetadata = ViewDto & { metadata: ViewMetadata };

export type WorkbookState = WorkbookLiveModeState &
  MapViewWorkbookState &
  WorkbookTextViewState &
  OnDemandMapState & {
    workbookConfiguration?: WorkbookConfigurationDto;
    workbookName: WorkbookName;
    /** Not present initially, if the hash is not in the URL. */
    workbookNameAndHash?: WorkbookNameAndHash;
    /** The ID of the active (currently shown) worksheet */
    activeWorksheetId: WorksheetId;
    /** The ID of the active view or '' if no view is active */
    activeViewId?: ViewId;
    /** The ID of the maximized view or '' if no view is maximized */
    maximizedViewId: ViewId | '';
    /** The ID of the enlarged view (e.g. "detail view") in linear layout mode or '' if no view is enlarged. */
    detailViewId: ViewId | '';
    /** workbook repository **/
    repository: RepositoryDto;
    /** The print name of the workbook */
    printName: string;
    /** The version of the workbook (undefined if the workbook is unsaved) */
    version?: WorkbookVersion;
    publicationStage?: PublicationStage;
    saved: boolean;
    /**
     * Whether the workbook is changed, i.e. whether a command was executed since it was last saved.
     *
     * _Note:_ An unsaved workbook is considered changed.
     */
    changed: ChangedState;
    /**
     * Whether the workbook was changed on the client
     *
     * _Note:_ This is a hack for changes that are not caused by a command execution
     * but should still be persisted on save.
     */
    clientChanged: boolean;
    /** Privileges of the acting user on the workbook */
    privileges: ObjectPrivilege[];
    /** Type of the workbook */
    workbookType: WorkbookType;
    /** Project of the workbook */
    project?: ProjectId;
    /** The workbook's settings */
    settings: Settings;
    /** The workbook config */
    config?: WorkbookConfigDto;
    /** The status of the initial workbook state fetching (see `fetchInitialState()`) */
    initializationStatus: STATUS;
    /** The status of the current command execution (see `executeCommand()`) or 'pending' if no command is being executed */
    commandStatus: STATUS;
    /**
     * The error key of the previously executed command error '' if there was no error
     *
     * Used to show specific messages for certain errors.
     *
     * @see problemType in WorkbookExceptionHandler.java
     */
    commandErrorKey: string;
    /** The error detail of the previously executed command error (if any) */
    commandErrorDetail?: unknown;
    /** Projects of the objecttypes that were added to the workbook and are components of the workbook's project plus
     * the project of the workbook */
    projectById: Record<ProjectIdString, ProjectDto>;
    /** Datasources of the objecttypes that were added to the workbook */
    dataSources: WorkbookDataSourceDto[];
    /** Objecttypes that were added to the workbook by ID */
    objecttypeById: Record<ObjecttypeIdString, Objecttype>;
    /** list of unavailable objecttypes that are still added to the workbook (e.g. deleted from repository) */
    unavailableObjecttypeIds: ObjecttypeIdString[];
    /** For each base OT, the other OTs that are reachable, including cardinality of the associations */
    reachableObjecttypesByBasicObjecttypeId: Partial<Record<ObjecttypeIdString, BasicObjecttypeReachabilityDto>>;
    /**
     * Self-Service objecttypes can logically depend on other objecttypes, i.e. if a dependency updates its data,
     * the dependent objecttype should be revised and marked as out of date in a logical order. Mostly relevant
     * for enrichments and copied objecttypes.
     */
    objecttypeDataDependencies: ObjecttypesDataDependenciesDto;
    /** Data protection of the workbook */
    dataProtection?: DataProtectionDto;
    /** Attributes of the objecttypes that were added to the workbook by ID */
    attributeById: Record<AttributeId, WorkbookAttributeDto>;
    /** Attributes that are active (used) in one of the workbook's data views by (active!) ID */
    activeAttributeById: Record<ActiveAttributeId, WorkbookAttributeDto>;
    /** The current dimension/measure value selection in the workbook or undefined if there's no selection */
    selection?: WorkbookSelectionDto;
    /** Selection in external layer (e.g. wms, arcgis) */
    selectionExternalLayer?: SelectionInLayerDto;
    /** Selection in other layers */
    selectionInOtherLayers?: SelectionInOtherLayersDto;
    /** The IDs of the worksheets of the workbook (in order) */
    worksheets: WorksheetId[];
    /** The worksheets of the workbook by ID */
    worksheetById: Record<WorksheetId, WorksheetDto>;
    /** The views of the workbook by ID */
    viewById: Record<ViewId, ViewDto>;
    /**
     * Metadata of the views of the workbook by ID
     *
     * _Note:_ These client-side metadata are currently separate from the actual views
     * so that they survive view updates from the server.
     */
    viewMetadataById: Record<ViewId, ViewMetadata>;
    /** IDs of views and data views that need to be refreshed */
    invalidViewsOrDataViews: (ViewId | DataViewId)[];
    /** IDs of layers by view id that needs to be refreshed */
    invalidLayerIdsByViewId: Record<ViewId, LayerId[]>;
    /** IDs of data views that have an outdated selection state */
    dataViewsAffectedBySelectionChange: DataViewId[];
    /** The data views of the workbook by ID */
    dataViewById: Record<DataViewId, DataViewDto>;
    /** controller view values by data view id */
    controllerValueByDataViewId: Record<DataViewId, ControllerViewOptionValuesDto>;
    /** The current notifications for data views, by view ID */
    notificationsByView: Record<ViewId, Record<WorkbookNotificationId, WorkbookNotification>>;
    /** The curated reports of the workbook By ID */
    reportById: Record<ReportId, ReportDto>;
    /** The embedding targets of the workbook by ID */
    embeddingTargetById: Record<WorkbookEmbeddingTargetId, WorkbookEmbeddingTargetDto>;
    /**
     * Workbook specific drill-through definitions
     * (not including the legacy drill-throughs defined via Cadenza Desktop)
     */
    drillThroughTargets: (WorksheetDrillThroughDto | ViewDrillThroughDto)[];
    /** List of condition IDs and condition group IDs that defines the order within the global analysis context */
    conditionsAndGroups: (ConditionId | ConditionGroupId)[];
    /** All conditions of the workbook by ID (global and locals ones) */
    conditionById: Record<ConditionId, ConditionDto>;
    /** Views and data views affected by a condition by condition ID */
    affectedViewsByConditionId: Record<ConditionId, ConditionToViewsRelationsDto>;
    /** Views and data views affected by a hierarchy by condition ID */
    affectedViewsByHierarchyId: Record<HierarchyId, HierarchyToViewsRelationsDto>;
    /** Conditions groups of the workbook by ID (Always global, we don't have local condition groups.) */
    conditionGroupById: Record<ConditionGroupId, ConditionGroupDto>;
    /** Hierarchies that are controlled in the analysis context by ID */
    hierarchyById: Record<HierarchyId, AnalysisContextHierarchyDto>;
    /** The status between two data views **/
    dataViewToDataViewConnectionStatus: Record<string, DataViewToDataViewConnectionStatusDto>;
    /** The status between view and data views **/
    viewToDataViewConnectionStatus: Record<string, ViewToDataViewConnectionStatusDto>;
    /**
     * The workbook panel states by panel ID
     *
     * Panels are visible and not disabled by default.
     */
    panelById: PanelById;
    /** true if the workbook state is used from within a PDF report */
    reportMode: boolean;
    forceMaximizePreviousState?: { maximizedViewId: ViewId | ''; panelById: PanelById };
    displayState?: DisplayState;
    relation?: WorkbookRelation;
    /** ID of the layer (map or chart) which settings should be displayed in the designer, will be reset after first display */
    activeLayerId?: LayerId;

    /* Need to be in store, because we need to have separate one for store and secondaryStore */
    lastCommand?: Command;
  };

export const reloadObjecttypes = async (objecttypeIds: ObjecttypeId[]) => {
  const fetchInitial = getStore().dispatch(fetchInitialState());
  // invalidate dataViews explicitly as map dataViews wouldn't get refreshed due to special caching logic
  const invalidateDataViews = getStore().dispatch(invalidateDataViewsUsingObjecttypes(objecttypeIds));
  return Promise.allSettled([fetchInitial, invalidateDataViews]);
};

export const workbookSlice = createSlice({
  name: SLICE_PREFIX,
  // Make a copy because we'll reuse it when we want to reset state, and if not, it will be patched by immer and made immutable
  initialState: createInitialWorkbookState(),
  reducers: {
    ...coreReducers,
    ...panelReducers,
    ...liveModeReducers,
    ...mapViewReducers,
    ...onDemandMapReducers,
  },
  // The "pending", "fulfilled" and "rejected" actions are dispatched automatically.
  // Hence, the reducers go in a separate structure.
  extraReducers: (builder) => {
    addCoreExtraReducers(builder);
    addTextViewExtraReducers(builder);
  },
});

const { reducer, actions } = workbookSlice;
getStore().registerReducer(SLICE_PREFIX, reducer);

// Export reducer for testing
export { reducer };

// Action creators

export const {
  updateWorkbookMetadata,
  updateWorkbookChanged,
  updateViewMetadata,
  removeObjecttypes,
  togglePanelVisibility,
  hideAllPanels,
  activateView,
  maximizeView,
  forceMaximizeView,
  setDetailView,
  initializeWorksheet,
  unmarkViewAsDirty,
  invalidateViewsOrDataViews,
  invalidateDataViewsUsingObjecttype,
  invalidateDataViewsUsingObjecttypes,
  invalidateLayerIds,
  updateObjecttypeBoundState,
  updateObjecttypeBoundStateOnSave,
  updateWorkbookPrintName,
  removeNotifications,
  removeNotificationsBySource,
  replaceNotificationsBySource,
  addNotifications,
  resetActiveLayerId,
  setActiveLayerId,
  removeUnusedObjecttypeIds,
  refreshViewData,
  startLiveMode,
  stopLiveMode,
  setZoomRelatedViewId,
  removeZoomRelatedViewId,
  setTargetMapExtentAndScaleForCoupledView,
  synchronizeToLeftTopMostView,
  stopInteractionForCoupledView,
  setMainMapContextForOverviewMap,
  setMainMapExtentForOverviewMapViewId,
  loadOnDemandWorksheet,
  loadOnDemandLayers,
  onDemandLayerFinishedLoading,
  shouldConsiderDataViewOnDemandSettings,
} = actions;

export const deactivateView = () => activateView('');
export const minimizeView = () => maximizeView('');
export const resetDetailView = () => setDetailView('');
